import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useSearchParamsSelector,
  useSearchParamsMethods,
} from '../../shared/packages/searchParamsManager';
import { useModalMethods } from '../../shared/packages/modalManager';
import { useAcceptanceViewFlatsInvalidate } from '../../api/acceptanceViewFlats';
import {
  detailsPageModeMap,
  searchParamKeys,
  searchParamSelectors,
} from '../../shared/configs/searchParams';
import { useRegistrationForAcceptance } from '../../api/registrationForAcceptance';
import { useAcceptanceViewCalendarInvalidate } from '../../api/acceptanceViewCalendar';
import { useValueRef } from '../../../../hooks/useValueRef';
import { stateTypes } from './constants';
import { useReRegistrationForAcceptance } from '../../api/reRegistrationForAcceptance';
import { Controller } from './controller';
import { useCalendarManager } from '../queries/calendar';
import { useUpdateRegistration } from '../../api/updateRegistration';

const Context = createContext();

const {
  detailsPageMode: pageModeKey,
  acceptance: acceptanceKey,
  company: companyKey,
} = searchParamKeys;

const registerModes = [
  detailsPageModeMap.registration,
  detailsPageModeMap.reRegistration,
];

const RegistrationProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const searchParamsControl = useSearchParamsMethods();
  const modalsControl = useModalMethods();

  const invalidateRooms = useAcceptanceViewFlatsInvalidate();
  const invalidateIntervals = useAcceptanceViewCalendarInvalidate();
  const { mutateAsync: register } = useRegistrationForAcceptance();
  const { mutateAsync: reRegister } = useReRegistrationForAcceptance();
  const { mutateAsync: updateRegistration } = useUpdateRegistration();
  const calendarManager = useCalendarManager();

  const controller = useState(
    () =>
      new Controller({
        controllers: {
          modal: modalsControl,
          searchParams: searchParamsControl,
          api: {
            invalidateRooms,
            invalidateIntervals,
            register,
            reRegister,
            updateRegistration,
          },
        },
        queryClient,
        calendarManager,
      })
  )[0];

  useEffect(() => {
    controller.setQueryClient(queryClient);
  }, [controller, queryClient]);

  useEffect(() => {
    controller.setCalendarManager(calendarManager);
  }, [controller, calendarManager]);

  const pageMode = useSearchParamsSelector(searchParamSelectors[pageModeKey]);
  const acceptance = useSearchParamsSelector(
    searchParamSelectors[acceptanceKey]
  );
  const company = useSearchParamsSelector(searchParamSelectors[companyKey]);

  const { setParams, getParams } = searchParamsControl;

  useEffect(() => {
    const isRegisterMode = registerModes.includes(pageMode);

    if (isRegisterMode && controller.mode === 'idle') {
      setParams({ [pageModeKey]: undefined });
    }

    if (!isRegisterMode && controller.mode !== 'idle') {
      controller.reset();
    }
  }, [pageMode, controller, setParams]);

  useEffect(() => {
    const isRegisterMode = registerModes.includes(getParams(pageModeKey));

    if (isRegisterMode) {
      setParams({ [pageModeKey]: undefined });
      controller.reset();
    }
  }, [acceptance, company, controller, getParams, setParams]);

  return <Context.Provider value={controller}>{children}</Context.Provider>;
};

const typeToState = {
  [stateTypes.registerAvailability]: 'registerAvailabilityState',
  [stateTypes.address]: 'addressState',
  [stateTypes.owners]: 'ownersState',
  [stateTypes.selectedOwner]: 'selectedOwnerState',
  [stateTypes.addressesAndClient]: 'addressesAndClientState',
  [stateTypes.save]: 'saveState',
};

const useRegistrationSubscribe = ({ type, selector, isEqual = Object.is }) => {
  const controller = useContext(Context);

  const [value, setValue] = useState(() => {
    const payload = controller[typeToState[type]].state.payload;
    return typeof selector === 'function' ? selector(payload) : payload;
  });

  const stateRef = useValueRef({ selector, isEqual, value });

  useEffect(() => {
    return controller.subscribe(type, (payload) => {
      const { selector, isEqual, value } = stateRef.current;

      const next = typeof selector === 'function' ? selector(payload) : payload;

      if (!isEqual(value, next)) {
        setValue(next);
      }
    });
  }, [controller, type, stateRef]);

  return value;
};

const useRegistrationControllers = () => {
  const controller = useContext(Context);

  const controllers = useState(() => {
    return {
      reset: controller.reset,
      notify: controller.notify,
      refetchOwners: controller.refetchOwners,
      selectOwner: controller.selectedOwnerState.selectOwner,
      selectOwnerModalHandlers: controller.selectOwnerModalHandlers.handlers,
      setupIntervalModalHandlers:
        controller.setupIntervalModalHandlers.handlers,
    };
  })[0];

  return controllers;
};

export {
  RegistrationProvider,
  useRegistrationSubscribe,
  useRegistrationControllers,
};
